import type { FC, ReactElement, RefObject } from 'react';
import { useEffect } from 'react';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { Link, useLocation } from 'react-router-dom';
import { capitalize } from '@/helpers/stringConversion';
import { Insight } from '@/views/map/Insight/Insight';
import { Item } from '@/views/map/GroupedItems/Item';
import { useFetchNetworkGroupItemQuery } from '@/features/analysisApi';
import { useUserClientContext } from '@/app/clientContext';
import classes from './MapPopover.module.css';
import { HazardTreeMapPopover } from './HazardTreeMapPopover';
import { LayerType } from '@/views/map/layers/types';
import { ItemHeading } from '@/views/map/GroupedItems/ItemHeading';
import { GoogleEvents } from '@/features/googleEvents';
import { getAppConfig } from '@/app/config';
import { FOOT_TO_METER } from '@/helpers/distanceMetric';
import { useAppSelector } from '@/app/hooks';
import { type SelectedItem } from '@/features/analysisMapSlice';
import { SpanMapPopover } from './SpanMapPopover';
import { RedFlagWarningInfo } from '@/views/map/RedFlagWarning/RedFlagWarningInfo';
import TagManager from 'react-gtm-module';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { captureException } from '@sentry/react';
import type { GroupId } from '@/features/types';

const VALID_POPOVER_TYPES = [
  'poles',
  'scan',
  'tree_type',
  'trees_points',
  'red_flag_warning',
] as const;

type ValidPopoverType = (typeof VALID_POPOVER_TYPES)[number];

const SelectedItemMapPopover: FC<{
  type: ValidPopoverType;
  selectedItem: SelectedItem;
  selectedNetworkGroup?: string;
  selectedNetworkGroupId?: GroupId;
  spanNetworkGroupId?: GroupId;
  id?: string;
}> = ({
  type,
  selectedItem,
  selectedNetworkGroupId,
  spanNetworkGroupId,
  id,
}) => {
  const intl = useClientTerminology();
  const location = useLocation();
  useDocumentTitle({
    title: selectedItem.object?.properties?.displayName ?? undefined,
    paths: ['*'], // If this component is mounted, we'll always set the title
  });
  const { context } = useUserClientContext();

  const itemProps = selectedItem?.object?.properties || {};
  const coordinate = selectedItem?.coordinate;

  // Todo: This should be refactored so that it does not fire when the selected item is a red flag warning.
  const {
    data: groupItemData,
    refetch: groupItemRefetch,
    isUninitialized,
  } = useFetchNetworkGroupItemQuery(
    {
      item: `${encodeURIComponent(itemProps.group ?? '')}`,
      group_id: selectedNetworkGroupId,
    },
    {
      skip: selectedItem.layer?.id !== LayerType[LayerType.scan],
    }
  );

  useEffect(() => {
    if (
      selectedItem.layer?.id !== LayerType[LayerType.red_flag_warning] &&
      !isUninitialized
    ) {
      groupItemRefetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem.layer?.id === LayerType[LayerType.red_flag_warning]) {
      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.redFlagWarning.map.mapPolygonModalOpened,
        },
      });
    }
  });

  if (!selectedItem) {
    return null;
  }

  const formatNum = (num: number) =>
    context.analysis_config.metric === 'ft'
      ? intl.formatNumber(num, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          unit: 'foot',
          unitDisplay: 'short',
          style: 'unit',
        })
      : intl.formatNumber(num * FOOT_TO_METER, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          unit: 'meter',
          unitDisplay: 'short',
          style: 'unit',
        });

  const popoverTypes: Record<ValidPopoverType, ReactElement | null> = {
    poles: (
      <Link
        key={id ?? itemProps.poleID}
        to={{
          pathname: `/point/${id ?? itemProps.poleID}`,
          search: location.search,
        }}
        data-testid="map-popover-poles"
      >
        <Item
          headingComponent={
            <ItemHeading
              name="Pole name"
              displayName={itemProps.displayName ?? undefined}
              id={itemProps.poleID ?? id}
              url={`${getAppConfig().appUrl}/point/${itemProps.poleID}`}
              copyNameEvent={GoogleEvents.copy_pole_name}
              copyURLEvent={GoogleEvents.copy_pole_url}
            />
          }
          subheading="Pole"
          isFloating={true}
        >
          <div className={classes.poles}>
            {itemProps.displayHeightInFt && (
              <Insight
                name="Height"
                data={formatNum(itemProps.displayHeightInFt)}
              />
            )}
            {itemProps.structureType && (
              <Insight
                name="Structure type"
                data={capitalize(itemProps.structureType)}
              />
            )}
            {itemProps.structureWidthInFt !== undefined && (
              <Insight
                name="Structure width"
                data={formatNum(itemProps.structureWidthInFt)}
              />
            )}
            <Insight name="Overstory ID" data={itemProps.poleID} withCopy />
          </div>
        </Item>
      </Link>
    ),
    scan:
      groupItemData &&
      (groupItemData?.properties?.networkGroupId === spanNetworkGroupId ||
        selectedNetworkGroupId === spanNetworkGroupId) ? (
        // TODO: We shouldn't have to cast this: need to return a tighter type in `analysisApi.ts`
        <SpanMapPopover
          key={groupItemData.properties?.level3 as string}
          a={groupItemData.properties}
        />
      ) : null,
    tree_type: (
      <Item
        heading=""
        subheading="Tree type"
        isFloating={true}
        data-testid="map-popover-tree-type"
      >
        <Insight
          name="Type"
          data={`${itemProps.dn === 1 ? 'Deciduous' : 'Evergreen'}`}
        />
      </Item>
    ),
    trees_points:
      itemProps.type === 'hazard' ? (
        <HazardTreeMapPopover
          params={{
            ...itemProps,
            bufferInFt: itemProps.bufferInFt ?? undefined,
          }}
          coordinates={coordinate}
        />
      ) : (
        <Item
          heading={
            itemProps.type === 'declining' ? 'Declining tree' : 'Strike tree'
          }
          isFloating={true}
          data-testid="map-popover-trees-points"
        />
      ),
    red_flag_warning: (
      <Item
        heading={itemProps.title}
        subheading="Red Flag Warning"
        data-testid="map-popover-red-flag-warning"
        closeButton={true}
        closeButtonGoogleEvent={
          GoogleEvents.redFlagWarning.map.mapPolygonModalClosed
        }
        isFloating={true}
      >
        <RedFlagWarningInfo
          title={itemProps.title}
          coveredCircuits={itemProps.coveredCircuits}
          coveredDistance={itemProps.coveredDistance}
          issued={itemProps.issued}
          start={itemProps.start}
          end={itemProps.end}
          nwsLink={itemProps.nwsLink}
          isPopover={true}
        />
      </Item>
    ),
  };
  return popoverTypes[type] ?? null;
};

export const MapPopover: FC<{
  target: SelectedItem;
  popover?: RefObject<HTMLDivElement | null>;
}> = ({ target, popover }) => {
  const { selectedNetworkGroup, spanNetworkGroup } = useAppSelector(
    (s) => s.analysisMap
  );

  try {
    if (target && target.layer) {
      const isDecliningTrees = target.object?.properties?.type === 'declining';
      const treesInDecline =
        isDecliningTrees || target.object?.properties?.type === 'hazard';

      const layerId: ValidPopoverType = treesInDecline
        ? 'trees_points'
        : (target.layer.id as ValidPopoverType);

      // Only add popover if the type has been defined
      if (VALID_POPOVER_TYPES.includes(layerId))
        return (
          <div
            className={`${classes.popover} ${
              isDecliningTrees
                ? classes.popoverDeclining
                : classes.popoverDefault
            }`}
            ref={popover}
            id="popup-content"
          >
            <div>
              <SelectedItemMapPopover
                type={layerId}
                selectedItem={target}
                selectedNetworkGroupId={selectedNetworkGroup?.id}
                spanNetworkGroupId={spanNetworkGroup?.id}
                id={target.id}
              />
            </div>
          </div>
        );
    }
  } catch (e) {
    // Fail silently - we don't want the entire app to crash if the popover has a problem
    // We don't show an error boundary here because that'd be _more_ disruptive.
    // Instead, log it as an error to Sentry.
    captureException(e, {
      extra: {
        target,
      },
    });
  }
  return null;
};
